import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  Paper,
  CircularProgress,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Tooltip
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getListDevice } from 'src/features/deviceSlice';
import { getListDeviceType } from 'src/features/deviceTypeSlice';
import { getListProducts } from 'src/features/productSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  filterInput: {
    backgroundColor: 'white',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    }
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    overflow: 'hidden'
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    '& th': {
      fontWeight: 700,
      color: '#475461',
      fontSize: '13px',
      borderBottom: '1px solid #e0e3ef'
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f1f5f9 !important'
    }
  },
  tableCell: {
    fontSize: '14px',
    color: '#1e293b',
    padding: '16px',
    borderBottom: '1px solid #f1f5f9'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3)
  },
  emptyState: {
    padding: theme.spacing(10),
    textAlign: 'center',
    color: '#64748b'
  },
  tooltip: {
    backgroundColor: 'rgba(33, 37, 41, 0.98)',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.3)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    padding: 0,
    maxWidth: 'none'
  },
  arrow: {
    color: 'rgba(33, 37, 41, 0.98)'
  }
}));

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const cleanServer = (process.env.REACT_APP_IMG_SERVER || '').replace(/\/+$/, '');
  const cleanUrl = url.replace(/^\/+/, '');
  return `${cleanServer}/${cleanUrl}`;
};

const ProductHoverCard = ({ product }) => {
  const primaryImage = product.images?.find(i => i.is_primary) || product.images?.[0];
  const placeholderSvg = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" fill="%23f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="12" fill="%2394a3b8">Không có ảnh</text></svg>`;
  const imageUrl = primaryImage ? getImageUrl(primaryImage.url) : placeholderSvg;

  return (
    <Box p={1.5} display="flex" style={{ gap: 16, minWidth: 420, maxWidth: 500, color: '#fff' }}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{
          width: 120,
          height: 120,
          flexShrink: 0,
          backgroundColor: '#fff',
          borderRadius: 6,
          border: '1px solid rgba(255, 255, 255, 0.15)',
          overflow: 'hidden'
        }}
      >
        <img
          src={imageUrl}
          alt={product.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column" justifyContent="center" style={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#ffb3ba', fontSize: '14px', marginBottom: 6, whiteSpace: 'normal' }}>
          {product.name} {product.code ? `(${product.code})` : ''}
        </Typography>
        <Typography variant="body2" style={{ fontSize: 12, whiteSpace: 'pre-line', color: '#f8f9fa', lineHeight: 1.4 }}>
          <strong>Mô tả:</strong> {product.description || 'Chưa có mô tả'}
        </Typography>
        {product.includes && (
          <Typography variant="body2" style={{ fontSize: 12, whiteSpace: 'pre-line', color: '#e9ecef', marginTop: 4, lineHeight: 1.4 }}>
            <strong>Kèm theo:</strong> {product.includes}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const InventoryTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [subTab, setSubTab] = useState(0);
  const [page, setPage] = useState(1);
  const [deviceTypeId, setDeviceTypeId] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const PAGE_SIZE = 12;

  const { listDevice, totalDevice, isLoading } = useSelector(state => state.deviceSlice || {});
  const { listDeviceType } = useSelector(state => state.deviceTypeSlice || {});
  const { listProducts, total: totalProducts, statistic: productStatistic, isLoading: isProductLoading } = useSelector(state => state.productSlice || {});
  const { dataLogin } = useSelector(state => state.authSlice || {});

  const currentAgencyId = dataLogin?.agency_id || 1;

  useEffect(() => {
    dispatch(getListDeviceType());
  }, [dispatch]);

  useEffect(() => {
    setSearchInput('');
    setSearchKeyword('');
    setPage(1);
  }, [subTab]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchKeyword(searchInput ? searchInput.trim() : '');
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (subTab === 0) {
      dispatch(getListDevice({
        page,
        page_size: PAGE_SIZE,
        agency_id: currentAgencyId,
        device_type_id: deviceTypeId === 'all' ? undefined : deviceTypeId,
        keywords: searchKeyword || undefined
      }));
    } else {
      dispatch(getListProducts({
        page,
        page_size: PAGE_SIZE,
        keywords: searchKeyword || undefined
      }));
    }
  }, [dispatch, subTab, page, deviceTypeId, currentAgencyId, searchKeyword]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const totalPages = Math.ceil((totalDevice || 0) / PAGE_SIZE);

  return (
    <Box className={classes.root}>
      {/* Sub tabs & Filters */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid #e0e3ef"
        mb={3}
        flexWrap="wrap"
        style={{ gap: 16 }}
      >
        <Tabs
          value={subTab}
          onChange={(e, val) => setSubTab(val)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="Thiết bị"
            style={{ textTransform: 'none', fontWeight: 700, fontSize: 15, minWidth: 0 }}
          />
          <Tab
            label="Phụ kiện"
            style={{ textTransform: 'none', fontWeight: 700, fontSize: 15, minWidth: 0 }}
          />
        </Tabs>

        {subTab === 0 ? (
          <Box display="flex" alignItems="center" style={{ gap: 12, paddingBottom: 4 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Tìm kiếm Serial..."
              className={classes.filterInput}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <TextField
              select
              variant="outlined"
              size="small"
              className={classes.filterInput}
              value={deviceTypeId}
              onChange={(e) => {
                setDeviceTypeId(e.target.value);
                setPage(1);
              }}
              style={{ minWidth: 180 }}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null,
                  PaperProps: {
                    style: {
                      marginTop: 8,
                      maxHeight: 400
                    }
                  }
                }
              }}
            >
              <MenuItem value="all">Tất cả loại thiết bị</MenuItem>
              {listDeviceType && listDeviceType.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="subtitle2" style={{ color: '#475461', fontWeight: 600, marginLeft: 8, whiteSpace: 'nowrap' }}>
              Tổng số: <span style={{ color: '#D9042F', fontSize: '18px' }}>{totalDevice || 0}</span> thiết bị
            </Typography>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" style={{ gap: 12, paddingBottom: 4 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Tìm kiếm phụ kiện..."
              className={classes.filterInput}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <Typography variant="subtitle2" style={{ color: '#475461', fontWeight: 600, marginLeft: 8, whiteSpace: 'nowrap' }}>
              Tổng số: <span style={{ color: '#D9042F', fontSize: '18px' }}>{productStatistic?.total_quantity || productStatistic?.total_available_quantity || 0}</span> phụ kiện
            </Typography>
          </Box>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            {subTab === 0 ? (
              <TableRow>
                <TableCell align="center" style={{ width: 60 }}>STT</TableCell>
                <TableCell>Loại thiết bị</TableCell>
                <TableCell>Serial</TableCell>
                <TableCell>Imei</TableCell>
                <TableCell>Sim</TableCell>
                <TableCell>Phiên bản</TableCell>
                <TableCell>Phiên bản firmware</TableCell>
                <TableCell>Ngày sản xuất</TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell align="center" style={{ width: 60 }}>STT</TableCell>
                <TableCell>Mã phụ kiện</TableCell>
                <TableCell>Tên phụ kiện</TableCell>
                <TableCell>Kèm theo</TableCell>
                <TableCell style={{ textAlign: 'center' }}>Đơn vị</TableCell>
                <TableCell style={{ textAlign: 'center' }}>Tồn kho</TableCell>
                <TableCell>Ngày tạo</TableCell>
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {subTab === 0 ? (
              listDevice && listDevice.length > 0 ? (
                listDevice.map((device, index) => (
                  <TableRow key={device.id} className={classes.tableRow} style={{ opacity: isLoading ? 0.6 : 1 }}>
                    <TableCell align="center" className={classes.tableCell}>
                      {(page - 1) * PAGE_SIZE + index + 1}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device?.deviceType?.name || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device.serial || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device.imei || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device.sim || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device.hardware_version || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device.firmware_version || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {device.date_of_manufacture
                        ? moment(device.date_of_manufacture).format('DD/MM/YYYY')
                        : '--'}
                    </TableCell>
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className={classes.emptyState}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      Đang tải dữ liệu...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className={classes.emptyState}>
                    <Typography variant="body1">Không có thiết bị trong kho</Typography>
                  </TableCell>
                </TableRow>
              )
            ) : (
              listProducts && listProducts.length > 0 ? (
                listProducts.map((product, index) => (
                  <TableRow key={product.id} className={classes.tableRow} style={{ opacity: isProductLoading ? 0.6 : 1 }}>
                    <TableCell align="center" className={classes.tableCell}>
                      {(page - 1) * PAGE_SIZE + index + 1}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Tooltip
                        title={<ProductHoverCard product={product} />}
                        placement="right"
                        arrow
                        classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                        interactive
                      >
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, cursor: 'pointer' }}>
                          {product.code || '--'}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {product.name || '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {product.includes || '--'}
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell}>
                      {product.unit || '--'}
                    </TableCell>
                    <TableCell align="center" className={classes.tableCell} style={{ fontWeight: 600 }}>
                      {product.available_quantity ?? product.stock_available ?? '--'}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {product.created_at
                        ? moment(product.created_at).format('DD/MM/YYYY')
                        : '--'}
                    </TableCell>
                  </TableRow>
                ))
              ) : isProductLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyState}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                      Đang tải dữ liệu...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyState}>
                    <Typography variant="body1">Không có phụ kiện trong kho</Typography>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {((subTab === 0 && listDevice && listDevice.length > 0) || (subTab === 1 && listProducts && listProducts.length > 0)) && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          style={{ width: '100%' }}
          p={2}
          borderTop="1px solid #eee"
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <Typography style={{ fontSize: '14px', color: '#475461', fontWeight: 500 }}>
              Tổng: {subTab === 0 ? (totalDevice || 0) : (totalProducts || 0)}
            </Typography>
            <Pagination
              count={subTab === 0 ? totalPages : Math.ceil((totalProducts || 0) / PAGE_SIZE)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              siblingCount={1}
              boundaryCount={2}
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default InventoryTable;
